/**
 * ───────────────────────────────────────────────────────────
 *  ResolutionCards
 * ───────────────────────────────────────────────────────────
 * Adds a scroll-driven “timeline” animation made of cards (.card)
 * interleaved with vertical lines.  Each element fades / scales into
 * place as it enters the viewport.
 *
 * DEPENDENCIES
 *   • GSAP v3 with the ScrollTrigger plugin.
 *   • An external `window.Sections` helper (e.g. Shopify’s Theme
 *     Editor) that calls the provided callback when the section
 *     is mounted.
 *
 * EXPECTED MARKUP
 * <section data-section-type="dynamic-timeline">
 *   <div id="cards-lines">
 *     <div class="card">…</div>
 *     <div class="card">…</div>
 *     …
 *   </div>
 * </section>
 *
 * INITIAL STATE (CSS)
 *   Place the class `.card_animation-init` on #cards-lines so that
 *   all cards/lines start hidden or transformed (opacity:0,
 *   translateY, scale, etc.).  The animation will reset those
 *   values to their “visible” state.
 */
class ResolutionCards {
  /**
   * @param  ele
   *   The section object supplied by `window.Sections`.  
   *   Must expose `el`, the root DOM node for the section.
   */
  constructor(ele) {
    this.container = ele.el.querySelector('#cards-lines');
    if (!this.container) return;

    this.container.classList.add('card_animation-init');
    this.cards  = Array.from(this.container.querySelectorAll('.card'));
    this.lines  = [];
    this.cardsCopy = [];

    this.createLines();
  }

  /* ----------------------------------------------------------------
   * Dynamically inserts a <div class="line lineX"> between each pair
   * of cards, then re-hydrates the DOM order.
   * ---------------------------------------------------------------- */
  createLines() {
    if (!this.container || this.cards.length === 0) return;

    // 1. Wipe the container
    this.container.innerHTML = '';

    // 2. Iterate backwards so splice() doesn’t disturb indices
    for (let i = this.cards.length - 1; i > 0; i--) {
      const line = document.createElement('div');
      line.classList.add('line', `line${i}`);

      // Insert the line *after* card[i-1] (i.e. at position i)
      this.cards.splice(i, 0, line);
      this.lines.push(line);
    }

    // 3. Re-append all nodes in the new order
    this.cards.forEach(node => this.container.appendChild(node));

    // 4. Register GSAP ScrollTriggers
    this.createTriggers();
  }

  /* ----------------------------------------------------------------
   * Builds GSAP ScrollTrigger animations for:
   *   • A “big picture” hero element (if present)
   *   • Each individual card
   *   • Each line
   * ---------------------------------------------------------------- */
  createTriggers() {
    /* — Big hero image — */
    gsap.to('.big-picture', {
      scrollTrigger: {
        trigger: '.big-picture',
        start: '-300px center',
        end:   '-100px center',
        scrub: 1
      },
      duration: 1,
      scale:    1,
      opacity:  1
    });

    /* — Cards — */
    this.cardsCopy = this.container.querySelectorAll('.card');
    this.cardsCopy.forEach(card => {
      gsap.to(card, {
        scrollTrigger: {
          trigger: card,
          start: '-100px center',
          end:   '-100px center',
          scrub: 1
        },
        duration: 1,
        y:        0,
        scale:    1,
        opacity:  1,
        ease:     'none'
      });
    });

    /* — Lines — */
    this.lines.forEach(line => {
      gsap.to(line, {
        scrollTrigger: {
          trigger: line,
          start: '-100px center',
          end:   'bottom center',
          scrub: 1
        },
        duration: 1,
        scale:    1
      });
    });
  }
}

/* ───────────────────────────────────────────────────────────
 *  Section registration (executed once DOM is ready)
 * ─────────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  const sections = new window.Sections();

  // Bind ResolutionCards to every section of type “dynamic-timeline”
  sections.register('dynamic-timeline', section => new ResolutionCards(section));
});
